import type { StrictLocalizeFunction } from "@faire/localization/core/types";
import { useStrictLocalization } from "@faire/localization/react/useStrictLocalization";
import React, { NamedExoticComponent, RefAttributes } from "react";

import { SVGIcon } from "./SVGIcon";

// oxlint-disable-next-line @typescript-eslint/no-empty-object-type
export type SVGIconComponent<T extends {} = {}> = NamedExoticComponent<
  T & SVGIcon.IProps & RefAttributes<SVGElement>
>;

// NOTE: This needs to stay a function component otherwise downstream icon tooling will not be able
// to find icon nodes via ts-morph since it expects a function node. (We can't switch to variable node)

export function createSVGIcon(
  path: React.ReactNode,
  displayName: string,
  defaultProps?:
    | ((strictLocalize: StrictLocalizeFunction) => SVGIcon.IProps)
    | SVGIcon.IProps
): SVGIconComponent {
  const Component = React.memo(
    ({ ...props }: SVGIcon.IProps & React.RefAttributes<SVGElement>) => {
      const { strictLocalize } = useStrictLocalization();
      const unwrappedDefaultProps =
        typeof defaultProps === "function"
          ? defaultProps?.(strictLocalize)
          : defaultProps;

      const { className: defaultClass, titleAccess } =
        unwrappedDefaultProps ?? {};
      const { className: explicitClass } = props ?? {};
      const curriedClass = [defaultClass, explicitClass]
        .filter((c) => !!c)
        .join(" ");

      return (
        <SVGIcon
          titleAccess={titleAccess}
          {...unwrappedDefaultProps}
          {...props}
          className={curriedClass}
        >
          {path}
        </SVGIcon>
      );
    }
  );

  if (process.env.NODE_ENV !== "production") {
    Component.displayName = `${displayName}Icon`;
  }

  return Component;
}
